import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { useAutoScrollToRef } from "hooks/useAutoScrollToRef";
import type { RoutesListViewV3Props } from "./types";

export const RoutesListViewV3 = ({
  creationModeState,
}: RoutesListViewV3Props) => {
  const { t } = useTranslation();

  const {
    routes,
    activeIndex,
    selectRoute,
    removeRoute,
    pageMetersPerPdfPoint,
  } = creationModeState;

  const { containerRef, targetRef } = useAutoScrollToRef<
    HTMLDivElement,
    HTMLDivElement
  >({ resetDep: activeIndex });

  return (
    <div
      ref={containerRef}
      className="flex flex-col gap-2 overflow-y-auto flex-1 min-h-0 p-px"
    >
      {routes.map((route, index) => {
        const isActive = activeIndex === index;
        return (
          <div
            key={route.id}
            ref={isActive ? targetRef : undefined}
            role="button"
            tabIndex={0}
            onClick={() => selectRoute(index)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                selectRoute(index);
              }
            }}
            className={clsx(
              "rounded-lg border bg-white shrink-0 cursor-pointer focus:outline-none",
              isActive
                ? "border-blue-400 ring-1 ring-blue-400"
                : "border-gray-200 hover:border-gray-300",
            )}
          >
            <div
              className={clsx(
                "flex items-center justify-between px-3 py-2 border-b rounded-t-lg",
                isActive
                  ? "bg-blue-50 border-blue-200"
                  : "bg-gray-50 border-gray-200",
              )}
            >
              <span
                className={clsx(
                  "text-sm font-semibold",
                  isActive ? "text-blue-700" : "text-gray-700",
                )}
              >
                {t("smartSurveyor.routesCreation.routeLabel", {
                  index: index + 1,
                })}
              </span>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  removeRoute(index);
                }}
                aria-label={t("smartSurveyor.routesCreation.removeRoute")}
                className="text-sm px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700 transition-colors"
              >
                {t("common.delete")}
              </button>
            </div>
            <div
              className="p-3 space-y-3"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="space-y-1">
                <div className="text-xs text-gray-700">
                  {t("smartSurveyor.routesCreation.routeTypeField")}
                </div>
                <div className="text-sm text-gray-900">
                  {route.routeTypeName}
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-xs text-gray-700">
                  {t("smartSurveyor.routesCreation.metersField")}
                </div>
                <div className="text-sm text-gray-900">
                  {pageMetersPerPdfPoint > 0
                    ? `${(route.polylineLength * pageMetersPerPdfPoint).toFixed(2)} m`
                    : "—"}
                </div>
              </div>
              <div className="text-xs text-gray-500">
                {route.nodes.length} nodes • {Math.round(route.polylineLength)} pt
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
